import { Link } from "react-router-dom";
import WorkCard from "@/components/feature/WorkCard";
import ArticleCard from "@/components/feature/ArticleCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { animeList } from "@/mocks/anime";
import { articleList } from "@/mocks/articles";
import { categories, siteInfo, valueProps } from "@/mocks/site";
import type { AnimeWork, Article } from "@/types";

const featuredIds = ["xiari-huisheng", "longwen-jijia", "jian-yu-chenguang"];

export default function Home() {
  useSeo({
    title: "樱花动漫社 - 新番推荐·动漫盘点·观看指南",
    description:
      "樱花动漫社是专注动漫内容的资讯与推荐平台，提供每季新番速递、编辑精选推荐、经典作品盘点、作品解读与观看指南，帮助动漫爱好者高效发现值得一看的动画作品。",
    keywords: "动漫推荐,新番速递,动漫资讯,动漫盘点,观看指南",
    path: "/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: siteInfo.name,
        url: siteInfo.domain,
        description: siteInfo.description,
        inLanguage: "zh-CN",
        potentialAction: {
          "@type": "SearchAction",
          target: `${siteInfo.domain}/works?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteInfo.name,
        url: siteInfo.domain,
        email: siteInfo.email,
        description: siteInfo.intro,
      },
    ],
  });

  const featured = animeList.filter((item) =>
    featuredIds.includes(item.id)
  ) as AnimeWork[];
  const latest = animeList.slice(0, 8) as AnimeWork[];
  const columns = articleList.slice(0, 3) as Article[];

  return (
    <>
      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20illustration%20of%20a%20dreamy%20cherry%20blossom%20landscape%20with%20soft%20pink%20petals%2C%20rolling%20misty%20hills%20and%20warm%20golden%20sky%20at%20dusk%2C%20painterly%20texture%2C%20stylized%20non-photographic%20look%2C%20generous%20negative%20space%20for%20text%2C%20cinematic%20wide%20composition&width=1920&height=1080&seq=hero-home-sakura&orientation=landscape"
            alt="樱花与远山构成的动漫风格插画背景"
            width={1920}
            height={1080}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/55 via-foreground-950/45 to-foreground-950/65"></div>
        </div>

        <div className="relative mx-auto flex w-full max-w-5xl flex-col items-center px-4 py-20 text-center md:px-6 md:py-28">
          <span className="inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-background-50/10 px-4 py-1.5 text-xs text-background-50 backdrop-blur-sm">
            <i className="ri-flower-fill" aria-hidden="true"></i>
            动漫资讯 · 编辑推荐 · 观看指南
          </span>
          <h1 className="mt-6 font-heading text-3xl font-900 leading-tight text-background-50 md:text-5xl">
            把每一部值得看的动画，认真讲给你听
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-50/85 md:text-base">
            {siteInfo.intro}
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
            <Link
              to="/works"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
            >
              <i className="ri-stack-line" aria-hidden="true"></i>
              浏览作品库
            </Link>
            <Link
              to="/ranking"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-500 text-background-50 transition-colors hover:bg-background-50/10 sm:w-auto"
            >
              <i className="ri-award-line" aria-hidden="true"></i>
              看编辑精选榜
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/works?category=${cat.id}`}
                className="whitespace-nowrap rounded-full border border-background-50/25 bg-background-50/10 px-4 py-1.5 text-xs text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 编辑精选 */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHeading
            eyebrow="Editor's Pick"
            title="本周编辑部精选"
            desc="从作品库里挑出三部近期最值得一看的作品，附上我们推荐它的理由。"
          />
          <Link
            to="/works"
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-500 text-primary-700 transition-colors hover:text-primary-800"
          >
            查看全部作品
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      </section>

      {/* 最新收录 */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="Fresh"
            title="最新收录"
            desc="持续补充的作品资料，点击卡片进入详情页可以查看制作团队、题材标签与观影提示。"
          />
          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4">
            {latest.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        </div>
      </section>

      {/* 主题分类 */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="Themes"
          title="按心情找作品"
          desc="不确定看什么的时候，先选一种此刻需要的情绪，再进去慢慢挑。"
          align="center"
        />
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/works?category=${cat.id}`}
              className="group flex flex-col rounded-xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 hover:bg-primary-50"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${cat.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-600 text-foreground-950 group-hover:text-primary-700">
                {cat.name}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{cat.desc}</p>
              <span className="mt-4 flex items-center gap-1 text-xs font-500 text-primary-700">
                进入分类
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 为什么是樱花动漫社 */}
      <section className="bg-secondary-50">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="Our Way"
            title="我们怎么做内容"
            desc="不做资源搬运，只做整理与讲述。"
          />
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {valueProps.map((item) => (
              <div key={item.title} className="rounded-xl border border-secondary-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-600 text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 最新专栏 */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHeading
            eyebrow="Columns"
            title="最新专栏与资讯"
            desc="关于新番动态、制作幕后与作品解析的长文，帮助你更懂你在看的作品。"
          />
          <Link
            to="/articles"
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-500 text-primary-700 transition-colors hover:text-primary-800"
          >
            进入资讯专栏
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          {columns.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-4 md:px-6">
        <div className="grid gap-6 rounded-2xl bg-accent-50 p-8 md:grid-cols-[1.4fr_1fr] md:p-12">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900">
              <i className="ri-award-line" aria-hidden="true"></i>
              编辑精选榜
            </span>
            <h2 className="mt-4 font-heading text-2xl font-700 text-foreground-950 md:text-3xl">
              不知道从哪部开始？先看我们的主题榜单
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              按热血、治愈、科幻、恋爱与悬疑五个方向整理作品，每一条都写清楚推荐理由，帮你更快做决定。
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/ranking"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-500 text-accent-950 transition-colors hover:bg-accent-600"
              >
                打开精选榜
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-accent-300 px-6 py-3 text-sm font-500 text-accent-900 transition-colors hover:bg-accent-100"
              >
                了解樱花动漫社
              </Link>
            </div>
          </div>
          <div className="flex flex-col justify-center gap-4 rounded-xl border border-accent-200 bg-background-50 p-6">
            <p className="text-sm leading-relaxed text-foreground-700">
              「与其推荐你看不完的清单，不如帮你找到此刻真正想看的那一部。」
            </p>
            <p className="text-xs text-foreground-500">—— {siteInfo.name} 编辑部</p>
          </div>
        </div>
      </section>
    </>
  );
}